// Define a discount code (F-POS-02, spec/06 §8.4) in two steps — the code and what it takes off, then
// its limits — over `createDiscountCode`. A code is 4–24 of A–Z, 0–9 and hyphens, stored upper-case and
// unique in the organisation (a clash is the contract's `409`). Money is typed in rupees, sent as paise.

import type { CreateDiscountCodeRequest } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Button, FormSection, SegmentedControl, Stepper, TextField, useToast } from '@clavian/ui-web';
import { useMemo, useState, type FormEvent, type ReactNode } from 'react';

import { PROBLEM, problemCode } from '../../api/problems';
import { useMyVenues } from '../../api/queries';
import { useScopeIds } from '../../shell/scope';
import { Notice } from '../../ui';
import { moneyFrom } from '../money';
import { useServerErrors } from '../server-errors';
import { useCreateDiscountCode } from './queries';
import { endOfDayIso, percentToBps, startOfDayIso } from './terms';

const STEP_FIELDS: readonly (readonly string[])[] = [['code', 'value', 'cap'], ['minOrder', 'usageLimit', 'perPlayerLimit', 'dates', 'form']];

/** The request's fields -> the form's inputs, for a validation failure. */
const CODE_SERVER_FIELDS: Readonly<Record<string, string>> = {
  code: 'code',
  percentBps: 'value',
  amount: 'value',
  maxDiscount: 'cap',
  minOrder: 'minOrder',
  usageLimit: 'usageLimit',
  perPlayerLimit: 'perPlayerLimit',
  validFrom: 'dates',
  validUntil: 'dates',
};
const CODE = /^[A-Za-z0-9-]{4,24}$/;

export function DiscountCodeFlow({ onDone }: { readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const serverErrors = useServerErrors();
  const { show } = useToast();
  const ids = useScopeIds();
  const currency = ids.currency ?? 'INR';
  const myVenues = useMyVenues();
  const timezone = useMemo(() => (myVenues.data ?? []).find((venue) => venue.organizationId === ids.organizationId && (ids.venueId === undefined || venue.id === ids.venueId))?.timezone ?? 'Asia/Kolkata', [myVenues.data, ids]);
  const create = useCreateDiscountCode(ids.organizationId);

  const [step, setStep] = useState(0);
  const [code, setCode] = useState('');
  const [type, setType] = useState<'percent' | 'amount'>('percent');
  const [percent, setPercent] = useState('');
  const [amount, setAmount] = useState('');
  const [cap, setCap] = useState('');
  const [minOrder, setMinOrder] = useState('');
  const [usageLimit, setUsageLimit] = useState('');
  const [perPlayerLimit, setPerPlayerLimit] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const count = (text: string, max: number): number | null | 'bad' => {
    if (text.trim() === '') return null;
    const value = Number(text);
    return Number.isInteger(value) && value >= 1 && value <= max ? value : 'bad';
  };

  function validate(): Record<string, string> {
    const next: Record<string, string> = {};
    if (!CODE.test(code.trim())) next['code'] = t('pricing.codeFlow.codeInvalid');
    if (type === 'percent' && percentToBps(percent) === null) next['value'] = t('pricing.form.percentInvalid');
    if (type === 'amount') {
      const money = moneyFrom(amount, currency);
      if (money === null || money.amount <= 0) next['value'] = t('pricing.form.amountInvalid');
    }
    if (cap.trim() !== '' && moneyFrom(cap, currency) === null) next['cap'] = t('pricing.form.amountInvalid');
    if (minOrder.trim() !== '' && moneyFrom(minOrder, currency) === null) next['minOrder'] = t('pricing.form.amountInvalid');
    if (count(usageLimit, 1_000_000) === 'bad') next['usageLimit'] = t('pricing.codeFlow.usageInvalid');
    if (count(perPlayerLimit, 1000) === 'bad') next['perPlayerLimit'] = t('pricing.codeFlow.perPlayerInvalid');
    if (validFrom !== '' && validUntil !== '' && validUntil < validFrom) next['dates'] = t('pricing.form.datesOrder');
    return next;
  }

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const all = validate();
    if (step === 0) {
      const mine = Object.fromEntries(Object.entries(all).filter(([key]) => STEP_FIELDS[0]?.includes(key)));
      setErrors(mine);
      if (Object.keys(mine).length === 0) setStep(1);
      return;
    }
    setErrors(all);
    if (Object.keys(all).length > 0) {
      if (STEP_FIELDS[0]?.some((field) => field in all)) setStep(0);
      return;
    }
    const uses = count(usageLimit, 1_000_000);
    const perPlayer = count(perPlayerLimit, 1000);
    const capMoney = cap.trim() === '' ? null : moneyFrom(cap, currency);
    const minMoney = minOrder.trim() === '' ? null : moneyFrom(minOrder, currency);
    const percentBps = type === 'percent' ? percentToBps(percent) : null;
    const amountMoney = type === 'amount' ? moneyFrom(amount, currency) : null;
    const body: CreateDiscountCodeRequest = {
      code: code.trim().toUpperCase(),
      type,
      ...(percentBps === null ? {} : { percentBps }),
      ...(amountMoney === null ? {} : { amount: amountMoney }),
      ...(capMoney === null ? {} : { maxDiscount: capMoney }),
      ...(minMoney === null ? {} : { minOrder: minMoney }),
      ...(typeof uses === 'number' ? { usageLimit: uses } : {}),
      ...(typeof perPlayer === 'number' ? { perPlayerLimit: perPlayer } : {}),
      ...(validFrom === '' ? {} : { validFrom: startOfDayIso(validFrom, timezone) }),
      ...(validUntil === '' ? {} : { validUntil: endOfDayIso(validUntil, timezone) }),
    };
    create.mutate(body, {
      onSuccess: () => {
        show({ tone: 'ok', title: t('pricing.codeFlow.created', { code: body.code }) });
        onDone();
      },
      onError: (error) => {
        if (problemCode(error) === PROBLEM.conflict) {
          setErrors({ code: t('pricing.codeFlow.taken') });
          setStep(0);
        } else {
          const refused = serverErrors(error, t('pricing.codeFlow.refused'), CODE_SERVER_FIELDS);
          setErrors(refused);
          const first = STEP_FIELDS.findIndex((fields) => fields.some((field) => field in refused));
          if (first >= 0) setStep(first);
        }
      },
    });
  }

  return (
    <div className="console-stepper-flow">
      <Stepper
        current={step}
        onStepSelect={(index) => (index < step ? setStep(index) : undefined)}
        steps={[
          { id: 'code', label: t('pricing.codeFlow.stepCode') },
          { id: 'limits', label: t('pricing.codeFlow.stepLimits') },
        ]}
      />
      <form className="console-form" onSubmit={submit} noValidate>
        {errors['form'] !== undefined ? (
          <Notice tone="danger" role="alert">
            {errors['form']}
          </Notice>
        ) : null}
        {step === 0 ? (
          <FormSection key="code"
            forceOpen={errors['cap'] !== undefined}
            more={
              type === 'percent' ? (
                <TextField label={t('pricing.form.cap', { currency })} description={t('pricing.form.capHint')} showOptional numeric inputMode="decimal" value={cap} onChange={(e) => setCap(e.target.value)} error={errors['cap']} />
              ) : undefined
            }
          >
            <TextField
              label={t('pricing.codeFlow.code')}
              description={t('pricing.codeFlow.codeHint')}
              required
              autoComplete="off"
              spellCheck={false}
              className="console-promo-code-input"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              error={errors['code']}
            />
            <SegmentedControl<'percent' | 'amount'>
              label={t('pricing.form.type')}
              value={type}
              onValueChange={setType}
              block
              options={[
                { value: 'percent', label: t('pricing.form.typePercent') },
                { value: 'amount', label: t('pricing.form.typeAmount', { currency }) },
              ]}
            />
            {type === 'percent' ? (
              <TextField label={t('pricing.form.percent')} required numeric inputMode="decimal" value={percent} onChange={(e) => setPercent(e.target.value)} error={errors['value']} />
            ) : (
              <TextField label={t('pricing.form.amount', { currency })} required numeric inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} error={errors['value']} />
            )}
          </FormSection>
        ) : (
          <FormSection key="limits"
            forceOpen={errors['minOrder'] !== undefined || errors['perPlayerLimit'] !== undefined}
            more={
              <>
                <TextField label={t('pricing.form.minOrder', { currency })} showOptional numeric inputMode="decimal" value={minOrder} onChange={(e) => setMinOrder(e.target.value)} error={errors['minOrder']} />
                <TextField label={t('pricing.codeFlow.perPlayer')} showOptional numeric inputMode="numeric" value={perPlayerLimit} onChange={(e) => setPerPlayerLimit(e.target.value)} error={errors['perPlayerLimit']} />
              </>
            }
          >
            <TextField label={t('pricing.codeFlow.usageLimit')} description={t('pricing.codeFlow.usageHint')} showOptional numeric inputMode="numeric" value={usageLimit} onChange={(e) => setUsageLimit(e.target.value)} error={errors['usageLimit']} />
            <div className="console-form__row">
              <TextField label={t('pricing.form.validFrom')} type="date" showOptional value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
              <TextField label={t('pricing.form.validUntil')} type="date" showOptional value={validUntil} onChange={(e) => setValidUntil(e.target.value)} error={errors['dates']} />
            </div>
          </FormSection>
        )}
        <div className="console-form__actions console-form__actions--end">
          {step === 0 ? (
            <Button type="button" variant="ghost" onClick={onDone}>
              {t('common.cancel')}
            </Button>
          ) : (
            <Button type="button" variant="ghost" onClick={() => setStep(0)}>
              {t('common.back')}
            </Button>
          )}
          <Button type="submit" variant="primary" busy={create.isPending}>
            {step === 0 ? t('common.next') : t('pricing.codeFlow.create')}
          </Button>
        </div>
      </form>
    </div>
  );
}
